import type { CSSProperties } from "react";
import { Activity, ArrowRightLeft, Braces, ChartNoAxesCombined, Network, SearchCode } from "lucide-react";
import type { Locale } from "@/lib/i18n";
import { getResourceEditorialStrategy } from "@/lib/resource-editorial-strategy";
import { responsiveImage } from "@/lib/responsive-image";

export type ResourceCoverInput = {
  slug?: string | null;
  slug_es?: string | null;
  title_en?: string | null;
  title_es?: string | null;
  excerpt_en?: string | null;
  excerpt_es?: string | null;
  tags_en?: string[] | null;
  tags_es?: string[] | null;
  cluster?: string | null;
  content_type?: string | null;
  cover_image_url?: string | null;
  cover_image_url_es?: string | null;
};

export type ResourceHub = "performance" | "conversion" | "technical-seo" | "migration" | "development" | "general";

type HubTheme = {
  label: { en: string; es: string };
  eyebrow: string;
  code: string;
  accent: string;
  accentInk: string;
  gridSize: { compact: number; full: number };
  bars: number[];
  motif: MotifKind;
  Icon: typeof Activity;
};

type MotifKind = "wave" | "funnel" | "nodes" | "handoff" | "circuit" | "arcs";

export const RESOURCE_HUB_THEMES: Record<ResourceHub, HubTheme> = {
  performance: {
    label: { en: "Performance", es: "Performance" },
    eyebrow: "Core Web Vitals · storefront systems",
    code: "PERF",
    accent: "#7f98a4",
    accentInk: "#526a76",
    gridSize: { compact: 24, full: 34 },
    bars: [30, 52, 40, 74, 58],
    motif: "wave",
    Icon: Activity,
  },
  conversion: {
    label: { en: "CRO & Conversion", es: "CRO y Conversión" },
    eyebrow: "Journey · merchandising · measurement",
    code: "CRO",
    accent: "#b08d80",
    accentInk: "#6f544a",
    gridSize: { compact: 30, full: 44 },
    bars: [78, 62, 48, 34, 22],
    motif: "funnel",
    Icon: ChartNoAxesCombined,
  },
  "technical-seo": {
    label: { en: "Technical SEO", es: "SEO Técnico" },
    eyebrow: "Crawling · indexation · architecture",
    code: "SEO",
    accent: "#91a993",
    accentInk: "#5a705c",
    gridSize: { compact: 20, full: 28 },
    bars: [44, 44, 66, 44, 44],
    motif: "nodes",
    Icon: SearchCode,
  },
  migration: {
    label: { en: "Shopify Migration", es: "Migración Shopify" },
    eyebrow: "Data · redirects · controlled release",
    code: "MIG",
    accent: "#c7a16a",
    accentInk: "#85633a",
    gridSize: { compact: 34, full: 50 },
    bars: [26, 38, 54, 70, 86],
    motif: "handoff",
    Icon: ArrowRightLeft,
  },
  development: {
    label: { en: "Shopify Engineering", es: "Ingeniería Shopify" },
    eyebrow: "Apps · integrations · platform systems",
    code: "DEV",
    accent: "#8fa2ac",
    accentInk: "#566873",
    gridSize: { compact: 16, full: 22 },
    bars: [60, 32, 60, 32, 60],
    motif: "circuit",
    Icon: Braces,
  },
  general: {
    label: { en: "Shugert Resources", es: "Recursos Shugert" },
    eyebrow: "Senior Shopify operating knowledge",
    code: "RES",
    accent: "#a5b4ba",
    accentInk: "#526a76",
    gridSize: { compact: 26, full: 38 },
    bars: [38, 58, 44, 78, 64],
    motif: "arcs",
    Icon: Network,
  },
};

function HubMotif({ kind, accent }: { kind: MotifKind; accent: string }) {
  const stroke = `color-mix(in srgb, ${accent} 46%, transparent)`;
  const faint = `color-mix(in srgb, ${accent} 22%, transparent)`;

  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full opacity-40"
      viewBox="0 0 400 250"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
      fill="none"
    >
      {kind === "wave" ? (
        <>
          <path d="M-10 190 C 60 190, 80 120, 140 128 S 220 92, 270 60 S 350 34, 420 26" stroke={stroke} strokeWidth="1.5" />
          <path d="M-10 214 C 70 214, 96 158, 158 164 S 240 130, 300 100 S 370 78, 420 70" stroke={faint} strokeWidth="1" />
        </>
      ) : null}

      {kind === "funnel" ? (
        <>
          <path d="M20 26 H380 L318 88 H82 Z" stroke={stroke} strokeWidth="1.2" />
          <path d="M92 104 H308 L266 160 H134 Z" stroke={faint} strokeWidth="1.2" />
          <path d="M144 176 H256 L222 226 H178 Z" stroke={faint} strokeWidth="1" />
        </>
      ) : null}

      {kind === "nodes" ? (
        <>
          <path d="M200 34 V86 M200 86 H92 V142 M200 86 H308 V142 M92 142 V196 M308 142 V196 M92 168 H308" stroke={faint} strokeWidth="1" />
          {[[200, 34], [200, 86], [92, 142], [308, 142], [92, 196], [308, 196]].map(([cx, cy]) => (
            <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="4.5" stroke={stroke} strokeWidth="1.3" />
          ))}
        </>
      ) : null}

      {kind === "handoff" ? (
        <>
          <circle cx="88" cy="126" r="86" stroke={faint} strokeWidth="1" />
          <circle cx="300" cy="126" r="86" stroke={stroke} strokeWidth="1.2" />
          <path d="M138 96 H262 M240 78 L266 96 L240 114" stroke={stroke} strokeWidth="1.3" />
          <path d="M262 156 H138 M160 138 L134 156 L160 174" stroke={faint} strokeWidth="1.1" />
        </>
      ) : null}

      {kind === "circuit" ? (
        <>
          <path d="M-10 70 H120 V132 H236 V54 H420" stroke={faint} strokeWidth="1" />
          <path d="M-10 196 H86 V150 H196 V208 H420" stroke={stroke} strokeWidth="1.1" />
          <rect x="146" y="94" width="108" height="62" rx="8" stroke={faint} strokeWidth="1" />
          {[[120, 132], [236, 54], [86, 150], [196, 208]].map(([cx, cy]) => (
            <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="3.5" fill={stroke} />
          ))}
        </>
      ) : null}

      {kind === "arcs" ? (
        <>
          <circle cx="352" cy="-16" r="128" stroke={faint} strokeWidth="1" />
          <circle cx="34" cy="252" r="122" stroke={stroke} strokeWidth="1" />
        </>
      ) : null}
    </svg>
  );
}

function haystack(resource: ResourceCoverInput, locale: Locale) {
  const tags = locale === "es" && resource.tags_es?.length ? resource.tags_es : resource.tags_en;
  return [
    resource.cluster,
    resource.slug,
    resource.slug_es,
    resource.title_en,
    resource.title_es,
    resource.excerpt_en,
    resource.excerpt_es,
    ...(tags ?? []),
  ].filter(Boolean).join(" ").toLowerCase();
}

export function inferResourceCoverCluster(resource: ResourceCoverInput, locale: Locale): ResourceHub {
  if (resource.cluster && resource.cluster in RESOURCE_HUB_THEMES) return resource.cluster as ResourceHub;
  const text = haystack(resource, locale);
  if (/performance|speed|core web vitals|\blcp\b|\binp\b|\bcls\b|rendimiento|velocidad/.test(text)) return "performance";
  if (/migration|migraci[oó]n|replatform|woocommerce|magento|bigcommerce|prestashop/.test(text)) return "migration";
  if (/technical seo|seo t[eé]cnico|crawl|indexation|indexaci[oó]n|canonical|schema|structured data|hreflang|rendering/.test(text)) return "technical-seo";
  if (/cro|conversion|conversi[oó]n|checkout|product page|\bpdp\b|cart|carrito|merchandising/.test(text)) return "conversion";
  if (/development|desarrollo|liquid|hydrogen|headless|function|custom app|integraci[oó]n|erp|b2b/.test(text)) return "development";
  return "general";
}

const SLUG_CARD_TITLES: Record<string, { en: string; es: string }> = {
  "shopify-collection-pages-not-ranking": {
    en: "Collection Pages That Rank",
    es: "Collections que rankean",
  },
};

const COVER_STOPWORDS = new Set([
  // EN
  "the", "a", "an", "that", "which", "actually", "really", "how", "why", "what", "when",
  "of", "for", "to", "in", "on", "with", "and", "or", "your", "our", "their", "is", "are",
  "be", "can", "will", "too", "so", "it", "its", "you", "we", "from", "into", "at", "by",
  // ES
  "el", "la", "los", "las", "un", "una", "unos", "unas", "que", "de", "del", "para", "por",
  "con", "en", "y", "o", "su", "sus", "tu", "tus", "al", "lo", "se", "es", "son", "como",
  "cuando", "por qué", "porque", "realmente",
]);

function condense(text: string, limit: number) {
  const words = text.split(/\s+/).filter(Boolean);
  const meaningful = words.filter((word) => !COVER_STOPWORDS.has(word.toLowerCase().replace(/[^\p{L}\p{N}]/gu, "")));
  const source = meaningful.length ? meaningful : words;
  return source.slice(0, limit).join(" ").replace(/[.,;:]$/, "");
}

function shortTitle(resource: ResourceCoverInput, locale: Locale) {
  const slug = resource.slug ?? resource.slug_es ?? "";
  const slugOverride = SLUG_CARD_TITLES[slug]?.[locale] ?? SLUG_CARD_TITLES[slug]?.en;
  if (slugOverride) return slugOverride;

  const strategy = getResourceEditorialStrategy(resource);
  const cardTitle = strategy.cardTitle?.[locale] ?? strategy.cardTitle?.en;
  if (cardTitle) return cardTitle;

  const title = locale === "es"
    ? strategy.displayTitle?.es || resource.title_es || resource.title_en || resource.slug_es || resource.slug || "Shugert Resource"
    : strategy.displayTitle?.en || resource.title_en || resource.slug || "Shugert Resource";

  const normalized = String(title).replace(/\s+/g, " ").trim();
  const match = normalized.match(/^(.*?)\s*[:|–—]\s*(.+)$/);
  const head = (match ? match[1] : normalized).replace(/[.,;]$/, "").trim();
  const tail = match ? match[2].trim() : "";

  const headWords = head.split(/\s+/).filter(Boolean);

  // Head alone is descriptive enough.
  if (headWords.length >= 3) return headWords.length <= 7 ? head : condense(head, 6);

  // Short head (e.g. "Shopify CRO") — borrow the most meaningful words from the subtitle.
  if (tail) {
    const extra = condense(tail, Math.max(2, 4 - headWords.length));
    if (extra) return `${head}: ${extra}`;
  }

  return condense(normalized, 7) || head || "Shugert Resource";
}


function typeLabel(type: string | null | undefined, locale: Locale) {
  const values: Record<string, { en: string; es: string }> = {
    pillar: { en: "Playbook", es: "Playbook" },
    tactical: { en: "Practical guide", es: "Guía práctica" },
    buyer_intent: { en: "Decision guide", es: "Guía de decisión" },
    field_note: { en: "Field note", es: "Field note" },
    case_note: { en: "Case note", es: "Caso" },
  };
  return (values[type ?? ""] ?? { en: "Resource", es: "Recurso" })[locale];
}

function stableIndex(resource: ResourceCoverInput) {
  const input = String(resource.slug ?? resource.slug_es ?? resource.title_en ?? resource.title_es ?? "resource");
  let hash = 0;
  for (const char of input) hash = (hash * 31 + char.charCodeAt(0)) % 997;
  return String((hash % 99) + 1).padStart(3, "0");
}

export function ResourceCover({
  resource,
  locale,
  compact = false,
  className = "",
}: {
  resource: ResourceCoverInput;
  locale: Locale;
  compact?: boolean;
  className?: string;
}) {
  const hub = inferResourceCoverCluster(resource, locale);
  const theme = RESOURCE_HUB_THEMES[hub];
  const Icon = theme.Icon;
  const title = shortTitle(resource, locale);
  const label = theme.label[locale];
  const documentCode = `SGT / ${theme.code} / ${stableIndex(resource)}`;
  const style = {
    "--hub-accent": theme.accent,
    "--hub-accent-ink": theme.accentInk,
  } as CSSProperties;

  // Preserve the original editorial URL for Google/OG/schema while browsers
  // select transformed mobile WebP candidates from the existing Storage CDN.
  // Below-the-headline covers should not compete with H1/CSS/font paint.
  // Compact cards keep their lightweight CSS art and no extra image requests.
  const coverUrl = locale === "es"
    ? resource.cover_image_url_es || resource.cover_image_url
    : resource.cover_image_url;
  const image = responsiveImage(coverUrl, [480, 640, 960, 1280]);
  if (!compact && coverUrl && (coverUrl.startsWith("https://") || coverUrl.startsWith("http://"))) {
    return (
      <img
        src={coverUrl}
        srcSet={image.srcSet}
        sizes="(min-width: 1024px) 540px, calc(100vw - 48px)"
        alt={locale === "es" ? resource.title_es || resource.title_en || title : resource.title_en || title}
        width={1200}
        height={750}
        loading="lazy"
        fetchPriority="low"
        decoding="async"
        className={`block h-full w-full object-cover ${className}`}
      />
    );
  }

  return (
    <div
      className={`resource-cover relative isolate h-full w-full overflow-hidden bg-[var(--surface-authority)] text-[var(--shugert-off-white)] ${className}`}
      data-resource-hub={hub}
      style={style}
      aria-label={`${label}: ${title}`}
      role="img"
    >
      <div
        className="absolute inset-0 opacity-55"
        aria-hidden
        style={{
          backgroundImage:
            "linear-gradient(rgba(250,250,248,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(250,250,248,.05) 1px, transparent 1px)",
          backgroundSize: compact
            ? `${theme.gridSize.compact}px ${theme.gridSize.compact}px`
            : `${theme.gridSize.full}px ${theme.gridSize.full}px`,
        }}
      />

      <div
        className="absolute inset-0"
        aria-hidden
        style={{
          background: `radial-gradient(120% 100% at 88% 4%, color-mix(in srgb, ${theme.accent} 16%, transparent), transparent 62%), color-mix(in srgb, ${theme.accent} 5%, transparent)`,
        }}
      />

      <HubMotif kind={theme.motif} accent={theme.accent} />
      <div className="resource-cover__accent-line absolute -right-[8%] top-[46%] h-px w-[62%] -rotate-[28deg]" aria-hidden />

      <div className={`relative z-10 flex h-full flex-col gap-4 ${compact ? "p-5" : "p-7 sm:p-9"}`}>
        {/* Header band: hub label + type label + icon chip, no overlap */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p
              className="text-[9px] font-semibold uppercase tracking-[0.22em]"
              style={{ color: theme.accent }}
            >
              <span className="block truncate">{label}</span>
            </p>
            <p className="mt-1.5 block truncate font-mono text-[9px] font-semibold uppercase tracking-[0.18em] text-white/55">
              {typeLabel(resource.content_type, locale)}
            </p>
          </div>
          <div
            className={`resource-cover__icon shrink-0 rounded-2xl border ${compact ? "p-2" : "p-2.5"}`}
            aria-hidden
            style={{
              borderColor: `color-mix(in srgb, ${theme.accent} 55%, transparent)`,
              backgroundColor: `color-mix(in srgb, ${theme.accent} 14%, transparent)`,
              color: theme.accent,
            }}
          >
            <Icon className={compact ? "h-4 w-4" : "h-6 w-6"} />
          </div>
        </div>

        {/* Center band: title + eyebrow */}
        <div className="mt-auto">
          <p
            className={`font-display font-semibold leading-[1.04] tracking-tight text-white ${compact ? "text-lg" : "text-3xl sm:text-[2.1rem]"}`}
          >
            {title}
          </p>
          {!compact ? (
            <p className="mt-3 text-[10px] font-medium uppercase tracking-[0.16em] text-white/55">
              {theme.eyebrow}
            </p>
          ) : null}
        </div>

        {/* Footer band: bars + document code, own row */}
        <div className="flex items-end justify-between gap-4 border-t border-white/15 pt-3.5">
          <div className="min-w-0 font-mono text-[9px] font-semibold uppercase tracking-[0.16em] text-white/52">
            <span className="block truncate">{documentCode}</span>
            {!compact ? <span className="mt-1 block truncate">Shugert Engineering</span> : null}
          </div>
          <div className="flex shrink-0 items-end gap-1.5 opacity-75" aria-hidden>
            {theme.bars.map((height, index) => (
              <span
                key={`${height}-${index}`}
                className="w-1.5 rounded-full border bg-white/[0.06]"
                style={{
                  height: `${compact ? Math.max(8, height * 0.22) : Math.max(10, height * 0.42)}px`,
                  borderColor: `color-mix(in srgb, ${theme.accent} 35%, transparent)`,
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
